import { Layout, Typography, Empty, Spin } from "antd";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useState, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import { useGetLevelDStatistics } from "@hooks";
import { LevelDReportFilter, LevelDCandidateSummary } from "../types/leveld-reports";
import { LevelDReportFilters, LevelDOverallStats, LevelDCompetencyChart, LevelDCandidateTable, LevelDCandidateDetail, LevelDExportButton, LevelDParticipantCompetencyTable } from "@components";

export const LevelDReportsPage = () => {
  const { t } = useTranslation();

  // Default to last 30 days
  const defaultFilter = useMemo<LevelDReportFilter>(() => ({
    startDate: dayjs().subtract(30, 'day').startOf('day').toISOString(),
    endDate: dayjs().endOf('day').toISOString(),
  }), []);

  const [filter, setFilter] = useState<LevelDReportFilter>(defaultFilter);

  // Candidate detail drawer state
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { 
    data: statistics, 
    isLoading, 
    refetch,
    isError 
  } = useGetLevelDStatistics(filter);

  const handleFilterChange = (newFilter: LevelDReportFilter) => {
    setFilter(newFilter);
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleCandidateClick = useCallback((candidate: LevelDCandidateSummary) => {
    setSelectedCandidateId(candidate.examInstanceId);
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setSelectedCandidateId(null);
  }, []);

  const hasData = statistics && statistics.totalCandidates > 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 1 }}
    >
      <Layout>
        {/* Page Title with Export Button */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div style={{ display: "flex" }}>
            <div
              style={{
                width: 3,
                backgroundColor: "#722ED1",
                borderRadius: 100,
                marginRight: 12,
              }}
            />
            <Typography.Title level={2} style={{ margin: 0 }}>
              {t("levelDReports.title")}
            </Typography.Title>
          </div>
          {hasData && (
            <LevelDExportButton filter={filter} />
          )}
        </div>

        {/* Filters */}
        <LevelDReportFilters
          filter={filter}
          onFilterChange={handleFilterChange}
          onRefresh={handleRefresh}
          isLoading={isLoading}
        />

        {/* Content */}
        {isLoading ? (
          <div style={{ textAlign: "center", padding: 48 }}>
            <Spin size="large" />
            <Typography.Text type="secondary" style={{ display: "block", marginTop: 16 }}>
              {t("levelDReports.loading")}
            </Typography.Text>
          </div>
        ) : isError ? (
          <Empty
            description={t("levelDReports.errors.loadFailed")}
            style={{ marginTop: 48 }}
          />
        ) : hasData ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Overall Statistics */}
            <LevelDOverallStats
              statistics={statistics}
              isLoading={isLoading}
            />

            {/* Competency Performance Chart */}
            <LevelDCompetencyChart
              competencies={statistics?.competencyStatistics}
              overallCompetencies={statistics?.overallCompetencyStatistics}
              isLoading={isLoading}
            />

            {/* Participant Competency Points Table */}
            <LevelDParticipantCompetencyTable filter={filter} />

            {/* Candidate Table */}
            <LevelDCandidateTable
              filter={filter}
              onCandidateClick={handleCandidateClick}
            />
          </motion.div>
        ) : (
          <Empty
            description={t("levelDReports.noData")}
            style={{ marginTop: 48 }}
          />
        )}

        {/* Candidate Detail Drawer */}
        <LevelDCandidateDetail
          examInstanceId={selectedCandidateId}
          open={drawerOpen}
          onClose={handleDrawerClose}
        />
      </Layout>
    </motion.div>
  );
};
